import { ReactNode, useEffect, useState } from 'react';
import {
  ComboBox,
  Label,
  Input,
  Button as AriaButton,
  Popover,
  ListBox,
  ListBoxItem,
  FieldError,
  Text as AriaText,
  type Key,
} from 'react-aria-components';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Spinner } from './Spinner';
import { typography } from './Text';
import type { SelectOption } from './Select';
import { useT } from '../../modules/i18n';

export interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  hint?: ReactNode;
  errorMessage?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  noOptionsText?: string;
  fullWidth?: boolean;
  className?: string;
  /**
   * Switches the component into ASYNC mode: when provided, `options` is
   * assumed to already be server-filtered by the caller (e.g. a debounced
   * search hook) — this component reports every input keystroke via this
   * callback instead of client-filtering `options` itself, and disables
   * react-aria's built-in `contains` filter (otherwise it would re-filter
   * the already-narrowed server results against the raw input text, hiding
   * matches that don't literally contain it). Omit for the default,
   * unchanged client-side-filter behavior — every existing caller keeps
   * working exactly as before.
   */
  onInputChange?: (query: string) => void;
  /** Async mode only — shows a small spinner while the caller's search
   * request is in flight. Ignored when `onInputChange` is not provided. */
  isLoading?: boolean;
}

const popover =
  'w-(--trigger-width) rounded-lg border border-default bg-surface p-1 shadow-lg outline-none ' +
  'entering:animate-in entering:fade-in-0 entering:zoom-in-95 exiting:animate-out exiting:fade-out-0';

const item =
  `flex cursor-pointer select-none items-center justify-between gap-3 rounded-md px-2.5 py-2 ${typography.body} text-fg-secondary outline-none ` +
  'data-[focused]:bg-muted-strong data-[selected]:text-fg data-[selected]:font-medium';

/**
 * A type-to-filter select for long lists (services, owners, repos) — react-aria
 * `ComboBox`. Same bordered surface, focus ring and label as TextInput, with a
 * search glyph and a filtered popup. Filtering, keyboard nav and ARIA come
 * from react-aria.
 */
export const SearchableSelect = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  hint,
  errorMessage,
  isRequired,
  isInvalid,
  isDisabled,
  noOptionsText,
  fullWidth,
  className,
  onInputChange,
  isLoading,
}: SearchableSelectProps) => {
  const t = useT();
  const isAsync = Boolean(onInputChange);
  const resolvedPlaceholder =
    placeholder ?? t('ui.searchableSelect.placeholder');
  const resolvedNoOptionsText =
    noOptionsText ?? t('ui.searchableSelect.noOptionsText');

  // Async mode controls the input itself so react-aria's own `contains`
  // filter never re-narrows the already server-filtered `options` — see the
  // prop doc. Kept local (not derived from `value`/`options` on every
  // render) so typing stays fully responsive.
  const [inputValue, setInputValue] = useState('');

  // Keep the visible text in sync when the SELECTED value changes from
  // outside (e.g. the filter/URL state resets), by resolving it against the
  // currently-known `options`. Typing itself must never be overridden by
  // this, so it only reacts to `value` changing, not to `options` churn.
  useEffect(() => {
    if (!isAsync) return;
    if (!value) {
      setInputValue('');
      return;
    }
    const match = options.find(o => o.value === value);
    if (match) setInputValue(match.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAsync, value]);

  const handleInputChange = (text: string) => {
    setInputValue(text);
    onInputChange?.(text);
  };

  const handleSelectionChange = (key: Key | null) => {
    const nextValue = key ? String(key) : '';
    onChange(nextValue);
    if (isAsync) {
      const match = options.find(o => o.value === nextValue);
      setInputValue(match ? match.label : '');
    }
  };

  return (
    <ComboBox
      aria-label={label}
      selectedKey={value || null}
      isRequired={isRequired}
      isInvalid={isInvalid ?? Boolean(errorMessage)}
      isDisabled={isDisabled}
      menuTrigger="focus"
      onSelectionChange={handleSelectionChange}
      {...(isAsync
        ? {
            inputValue,
            onInputChange: handleInputChange,
            // Options are already server-filtered — never re-filter them
            // against the raw input text.
            defaultFilter: () => true,
          }
        : {})}
      className={cx(
        'group flex flex-col gap-1.5',
        fullWidth && 'w-full',
        className,
      )}
    >
      {label && (
        <Label className={cx(typography.label, 'text-fg-secondary')}>
          {label}
          {isRequired && <span className="ml-0.5 text-brand-text">*</span>}
        </Label>
      )}
      <div
        className={cx(
          'relative flex h-10 items-center rounded-lg border border-strong bg-surface pl-3 pr-9 shadow-xs transition',
          'focus-within:border-brand-300 focus-within:ring-4 focus-within:ring-[rgb(var(--c-ring)/0.24)]',
          'group-data-[invalid]:border-error-500',
        )}
      >
        {isAsync && isLoading ? (
          <Spinner size="sm" className="mr-2 shrink-0" />
        ) : (
          <Icon
            as={Search}
            size="sm"
            strokeWidth={1.75}
            className="mr-2 shrink-0 text-fg-muted"
          />
        )}
        <Input
          placeholder={resolvedPlaceholder}
          className={cx(
            typography.body,
            'min-w-0 flex-1 appearance-none border-0 bg-transparent text-fg outline-none placeholder:text-fg-muted',
          )}
        />
        {/* Absolutely pinned inside the box so it can never escape the border,
          regardless of the flex layout. */}
        <AriaButton className="absolute inset-y-0 right-0 flex w-9 cursor-pointer appearance-none items-center justify-center border-0 bg-transparent text-fg-muted outline-none">
          <Icon as={ChevronDown} size="sm" />
        </AriaButton>
      </div>
      {hint && !errorMessage && (
        <AriaText
          slot="description"
          className={cx(typography.body, 'text-fg-tertiary')}
        >
          {hint}
        </AriaText>
      )}
      <FieldError className={cx(typography.body, 'text-error')}>
        {errorMessage}
      </FieldError>
      <Popover className={popover}>
        <ListBox
          items={options}
          renderEmptyState={() => (
            <div
              className={cx(typography.body, 'px-2.5 py-2 text-fg-tertiary')}
            >
              {resolvedNoOptionsText}
            </div>
          )}
          className="max-h-80 overflow-auto outline-none"
        >
          {(option: SelectOption) => (
            <ListBoxItem
              id={option.value}
              textValue={option.label}
              className={item}
            >
              {({ isSelected }) => (
                <>
                  <span className="flex min-w-0 items-center gap-2">
                    {option.icon}
                    <span className="truncate" title={option.label}>
                      {option.label}
                    </span>
                  </span>
                  {isSelected && (
                    <Icon
                      as={Check}
                      size="sm"
                      strokeWidth={2.5}
                      className="shrink-0 text-brand-text"
                    />
                  )}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </ComboBox>
  );
};
